import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, ROLE_LABELS, fmtDate } from '../lib/format';

const EMPTY = { name: '', email: '', password: '', role: 'capturista', activo: true };

export default function UsuariosPage() {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/users')).data,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (editId && !payload.password) delete payload.password;
      if (editId) return (await api.put(`/users/${editId}`, payload)).data;
      return (await api.post('/users', payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(editId ? 'Usuario actualizado' : 'Usuario creado');
      setDialogOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario eliminado');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: '', role: u.role, activo: u.activo !== false }); setEditId(u.id); setDialogOpen(true); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Usuarios</h2>
          <p className="text-sm text-muted-foreground">Gestión de usuarios y roles del sistema</p>
        </div>
        <Button onClick={openNew} data-testid="new-user-button">
          <Plus size={16} className="mr-1.5" /> Nuevo usuario
        </Button>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Nombre</TableHead>
                  <TableHead className="th-label">Correo</TableHead>
                  <TableHead className="th-label">Rol</TableHead>
                  <TableHead className="th-label">Estado</TableHead>
                  <TableHead className="th-label">Creado</TableHead>
                  <TableHead className="th-label w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(usuarios || []).map((u) => (
                  <TableRow key={u.id} className="hover:bg-background/40" data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}{u.id === me?.id && <span className="text-muted-foreground text-xs ml-2">(tú)</span>}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary" className="border border-border">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                    <TableCell>
                      {u.activo !== false
                        ? <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/15">Activo</Badge>
                        : <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border">Inactivo</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{fmtDate(u.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                          <Pencil size={14} />
                        </Button>
                        {u.id !== me?.id && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(u.id)} data-testid={`delete-user-${u.email}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Correo *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{editId ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrador">Administrador · acceso total</SelectItem>
                  <SelectItem value="editor">Editor · todo salvo eliminar y usuarios</SelectItem>
                  <SelectItem value="capturista">Capturista · solo captura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editId && (
              <div className="flex items-center justify-between">
                <Label>Usuario activo</Label>
                <Switch checked={form.activo} onCheckedChange={(v) => setForm((f) => ({ ...f, activo: v }))} data-testid="user-activo-switch" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.name || !form.email || (!editId && !form.password) || saveMutation.isPending} data-testid="save-user-button">
              {editId ? 'Guardar cambios' : 'Crear usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
